import { publicApi } from 'src/api/axios';

import { httpStatus, isHttpStatus } from '../../usePublicTokenResource';

/**
 * Public contract-signing API (ticket 06.5, contract doc §4 "Built (C1)").
 * Unauthenticated + tokenized: the dedicated contract token (NEVER the shared
 * enrollment token) arrives in the email link `/contract?t=<token>` and scopes
 * both calls. Uses `publicApi` (no JWT) like the W2 PHQ public-link resolve.
 *
 * This module consumes the wire shapes EXACTLY — no speculative aliases:
 *
 *   GET /api/w2/contract/:token →
 *     Pending: {state:'Pending', companyName, document, renderDigest, linkValidDays}
 *              (`document` is the server's single render source — the same object it
 *              iterates to produce the executed PDF; see `ContractDocument`)
 *     Signed:  {state:'Signed', companyName, signedAt}
 *              (one-shot; the link is CONSUMED at signature — no terms, no
 *              document and no executed-PDF download are ever served to it
 *              again. The employer's copy arrives by email; the CSR keeps the
 *              authenticated download.)
 *     Expired: 200 {state:'Expired', companyName}        (deliberate §4 carve-out)
 *     Unknown/rotated: 404 error.invalidToken
 *   POST /api/w2/contract/:token/sign
 *     {signerName, signature, date:'YYYY-MM-DD', authorized: true, federalTaxId,
 *      renderDigest}
 *     → 200 {state:'Signed', signedAt}
 *     → 409 error.contractAlreadySigned | 409 error.contractExpired
 *     → 400 error.contractSignatureMismatch | 404
 */

export type ContractTokenState = 'Pending' | 'Signed' | 'Expired';

/**
 * The structured contract document (contract v2) — the server's single render source, which
 * the backend also iterates to produce the executed PDF. The page renders these blocks
 * verbatim and holds no section text of its own, so what the prospect reads and what gets
 * filed cannot drift.
 *
 * These types are the page's RENDER CONTRACT, and `parseDocument` below enforces them
 * losslessly: anything this build cannot render faithfully — an unknown block kind, an
 * unknown key, a mistyped field — puts the page in a cannot-sign state rather than showing a
 * partial document. The signature it submits is against the server's digest of the WHOLE
 * document, so rendering less than all of it would sign terms the prospect never saw.
 *
 * Values the signer types never arrive on the wire; they are named SLOTS the page fills in
 * live as the inputs change (the tax ID especially: it is in no payload, in either direction).
 */
export type ContractSlot = 'federalTaxId' | 'signerName' | 'signatureName';

export interface ContractFieldRow {
  label: string;
  value: string | null;
  slot?: ContractSlot;
}

export type ContractBlock =
  | { kind: 'fields'; rows: ContractFieldRow[] }
  | { kind: 'paragraphs'; text: string[] }
  | { kind: 'marks'; items: Array<{ label: string; checked: boolean }> }
  | {
      kind: 'contributionGrid';
      rows: Array<{
        tierLabel: string;
        percentage: string | null;
        flatRate: string;
        derived: boolean;
      }>;
      footnote: string | null;
    }
  | {
      kind: 'planList';
      plans: Array<{ name: string; deductibleLine: string | null }>;
      note: string;
    }
  | { kind: 'statement'; text: string }
  | {
      kind: 'signature';
      attestation: string[];
      /** The two typed values are slots; the page live-fills them as the prospect types. */
      nameSlot: true;
      signatureSlot: true;
      /** Always `null` on a public payload — filled only when the server builds the PDF. */
      nameValue: null;
      signatureValue: null;
      dateValue: string;
      brokerName: string | null;
    };

export interface ContractSection {
  number: number;
  heading: string;
  subheading?: string;
  blocks: ContractBlock[];
}

export interface ContractDocument {
  title: string;
  termsVersion: string;
  documentReference: string;
  sections: ContractSection[];
}

/**
 * The render data the page consumes. This is an ALLOWLIST: `normalizeContract`
 * copies exactly these fields off the wire and drops everything else, so bank/
 * ACH data (or any future field) can never leak into the DOM even if the API
 * were to return it (§4: "NEVER bank/ACH data"). No tax ID — not in the system.
 */
export interface ContractRenderData {
  state: ContractTokenState;
  companyName: string;
  /** Pending only — the full 13-section document to render (contract v2). */
  document?: ContractDocument;
  /** Pending only — fingerprint of `document`, echoed back when signing. */
  renderDigest?: string;
  /** Pending only — how many days the signing link stays valid (page validity note). */
  linkValidDays?: number;
  signedAt?: string;
  /**
   * Pending only — the server sent a document this build cannot render faithfully (unknown
   * block kind, unknown key, mistyped field) or sent no digest to sign against. The page
   * shows a cannot-sign notice and NO document and NO sign box: signing here would mean
   * signing the server's digest of content that was never displayed.
   */
  cannotRender?: boolean;
}

export interface SignContractBody {
  signerName: string;
  signature: string;
  /** Signing date, ISO `yyyy-MM-dd` (today — set by the page, not typed). */
  date: string;
  authorized: true;
  /** The typed EIN (contract v2 §1) — sent only here, never rendered from a payload. */
  federalTaxId: string;
  /**
   * Fingerprint of the document that was actually displayed, so the server can refuse a
   * signature against a contract that changed while it was open (409
   * `error.contractDataChanged`). Required by the API.
   */
  renderDigest: string;
}

export interface SignContractResult {
  /** Server-recorded signing timestamp (§4 sign response). */
  signedAt?: string;
}

interface RawContract {
  state?: unknown;
  companyName?: unknown;
  document?: unknown;
  renderDigest?: unknown;
  linkValidDays?: unknown;
  signedAt?: unknown;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

/** numeric(12,2) columns serialize as strings — accept both. */
function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function normalizeState(raw: unknown): ContractTokenState | null {
  switch (raw) {
    case 'Pending':
    case 'Signed':
    case 'Expired':
      return raw;
    default:
      return null;
  }
}

const CONTRACT_SLOTS: ContractSlot[] = [
  'federalTaxId',
  'signerName',
  'signatureName',
];

/**
 * The document carries content this build cannot render faithfully. Never surfaced to the
 * user as-is — it puts the page in the cannot-sign state (see `cannotRender`).
 */
class UnrenderableDocument extends Error {}

const fail = (what: string): never => {
  throw new UnrenderableDocument(what);
};

const str = (v: unknown): string =>
  typeof v === 'string' ? v : fail('string');

const nullableStr = (v: unknown): string | null => (v === null ? null : str(v));

const bool = (v: unknown): boolean =>
  typeof v === 'boolean' ? v : fail('boolean');

const num = (v: unknown): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fail('number');

const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : fail('array'));

const strArray = (v: unknown): string[] => arr(v).map(str);

/**
 * A plain object whose keys are all named in `keys`. Rejecting UNKNOWN keys is what makes
 * the parse lossless: a field this build would silently ignore is content the signer would
 * not see, and the page must refuse rather than render around it.
 */
function obj(v: unknown, keys: string[]): Record<string, unknown> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return fail('object');
  const record = v as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!keys.includes(key)) fail(`key:${key}`);
  }
  return record;
}

/** Exactly the wire literal this build understands, or the document is unrenderable. */
const literal = <T>(v: unknown, expected: T): T =>
  v === expected ? expected : fail('literal');

/** ONE document block, parsed strictly — an unknown kind fails the whole document. */
function parseBlock(raw: unknown): ContractBlock {
  const kind = (raw as { kind?: unknown } | null)?.kind;
  switch (kind) {
    case 'fields': {
      const block = obj(raw, ['kind', 'rows']);
      return {
        kind: 'fields',
        rows: arr(block.rows).map((entry) => {
          const row = obj(entry, ['label', 'value', 'slot']);
          const slot =
            row.slot === undefined
              ? undefined
              : (CONTRACT_SLOTS.find((candidate) => candidate === row.slot) ??
                fail('slot'));
          return {
            label: str(row.label),
            value: nullableStr(row.value),
            ...(slot ? { slot } : {}),
          };
        }),
      };
    }
    case 'paragraphs': {
      const block = obj(raw, ['kind', 'text']);
      return { kind: 'paragraphs', text: strArray(block.text) };
    }
    case 'marks': {
      const block = obj(raw, ['kind', 'items']);
      return {
        kind: 'marks',
        items: arr(block.items).map((entry) => {
          const item = obj(entry, ['label', 'checked']);
          return { label: str(item.label), checked: bool(item.checked) };
        }),
      };
    }
    case 'contributionGrid': {
      const block = obj(raw, ['kind', 'rows', 'footnote']);
      return {
        kind: 'contributionGrid',
        rows: arr(block.rows).map((entry) => {
          const row = obj(entry, [
            'tierLabel',
            'percentage',
            'flatRate',
            'derived',
          ]);
          return {
            tierLabel: str(row.tierLabel),
            percentage: nullableStr(row.percentage),
            flatRate: str(row.flatRate),
            derived: bool(row.derived),
          };
        }),
        footnote: nullableStr(block.footnote),
      };
    }
    case 'planList': {
      const block = obj(raw, ['kind', 'plans', 'note']);
      return {
        kind: 'planList',
        plans: arr(block.plans).map((entry) => {
          const plan = obj(entry, ['name', 'deductibleLine']);
          return {
            name: str(plan.name),
            deductibleLine: nullableStr(plan.deductibleLine),
          };
        }),
        note: str(block.note),
      };
    }
    case 'statement': {
      const block = obj(raw, ['kind', 'text']);
      return { kind: 'statement', text: str(block.text) };
    }
    case 'signature': {
      const block = obj(raw, [
        'kind',
        'attestation',
        'nameSlot',
        'signatureSlot',
        'nameValue',
        'signatureValue',
        'dateValue',
        'brokerName',
      ]);
      return {
        kind: 'signature',
        attestation: strArray(block.attestation),
        nameSlot: literal(block.nameSlot, true as const),
        signatureSlot: literal(block.signatureSlot, true as const),
        // The page renders what the prospect types, so a server-filled value here would be
        // content it never shows — that is the executed-PDF shape, not a public payload.
        nameValue: literal(block.nameValue, null),
        signatureValue: literal(block.signatureValue, null),
        dateValue: str(block.dateValue),
        brokerName: nullableStr(block.brokerName),
      };
    }
    default:
      return fail(`kind:${String(kind)}`);
  }
}

/** Contract v2 `document` (§1.2), parsed losslessly — see `UnrenderableDocument`. */
function parseDocument(raw: unknown): ContractDocument {
  const source = obj(raw, [
    'title',
    'termsVersion',
    'documentReference',
    'sections',
  ]);
  const sections = arr(source.sections);
  if (sections.length === 0) fail('sections');
  const document: ContractDocument = {
    title: str(source.title),
    termsVersion: str(source.termsVersion),
    documentReference: str(source.documentReference),
    sections: sections.map((entry) => {
      const section = obj(entry, ['number', 'heading', 'subheading', 'blocks']);
      return {
        number: num(section.number),
        heading: str(section.heading),
        ...(section.subheading === undefined
          ? {}
          : { subheading: str(section.subheading) }),
        blocks: arr(section.blocks).map(parseBlock),
      };
    }),
  };
  if (
    !document.sections.some((section) =>
      section.blocks.some((block) => block.kind === 'signature')
    )
  ) {
    fail('signatureBlock');
  }
  return document;
}

/**
 * Allowlist normalizer for the ENVELOPE (see `ContractRenderData`): copies exactly the
 * fields the page consumes and drops everything else, so bank/ACH data (or any future
 * field) can never leak into the DOM even if the API returned it.
 *
 * The `document` is NOT normalized that way — it is parsed strictly. A Pending payload whose
 * document this build cannot render faithfully, or that arrives without a digest to sign
 * against, comes back as `cannotRender` and the page refuses to sign at all: the signature
 * is submitted against the SERVER's digest of the whole document, so rendering part of it
 * would mean signing terms that were never displayed.
 *
 * Returns `null` when the payload has no recognizable state — the caller treats that as an
 * invalid link.
 */
export function normalizeContract(
  data: RawContract | null | undefined
): ContractRenderData | null {
  if (!data) return null;
  const state = normalizeState(data.state);
  if (!state) return null;
  const base = {
    state,
    companyName: asString(data.companyName) ?? 'your company',
    signedAt: asString(data.signedAt),
  };
  if (state !== 'Pending') return base;

  const renderDigest = asString(data.renderDigest);
  try {
    const document = parseDocument(data.document);
    if (!renderDigest) fail('renderDigest');
    return {
      ...base,
      document,
      renderDigest,
      linkValidDays: asNumber(data.linkValidDays),
    };
  } catch (err) {
    if (!(err instanceof UnrenderableDocument)) throw err;
    // Reason-only (never the payload): enough to tell a dialect skew from a bug.
    console.error('[w2] contract document unrenderable', {
      reason: err.message,
    });
    return { ...base, cannotRender: true };
  }
}

/**
 * Dev-only mock seam so the page can be previewed without a backend:
 * `VITE_W2_CONTRACT_MOCK=1 npm run dev`, then
 * `/contract?t=mock-pending|mock-signed|mock-expired` (anything else → invalid).
 * The fixtures are RAW §4-shaped wire payloads (`./fixtures`, shared with the
 * e2e stubs) served through `normalizeContract` exactly like real responses, so
 * the preview exercises the allowlist seam. Dynamically imported so fixtures
 * never reach a production bundle.
 */
function isDevMock(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_W2_CONTRACT_MOCK === '1';
}

/**
 * Resolve the contract render data for a token. Returns `null` for unknown /
 * rotated tokens (the API 404s `error.invalidToken` — §4 hardening: no
 * existence oracle; genuinely expired emailed tokens come back as
 * `{state:'Expired'}`); throws only on unexpected transport errors so the page
 * can offer a retry instead of mislabeling an outage as a bad link.
 */
export async function getContract(
  token: string
): Promise<ContractRenderData | null> {
  if (isDevMock()) {
    return normalizeContract((await import('./fixtures')).mockResolve(token));
  }
  try {
    const res = await publicApi.get<RawContract>(
      `/w2/contract/${encodeURIComponent(token)}`
    );
    return normalizeContract(res.data);
  } catch (err) {
    if (isHttpStatus(err, [400, 404])) return null;
    // Unexpected outage → the caller shows a retry. Log status-only for
    // observability (never the payload/token) — finding M2.
    console.error('[w2] contract resolve failed', { status: httpStatus(err) });
    throw err;
  }
}

/**
 * Sign the contract (one-shot: signing CONSUMES the link — afterwards the token
 * resolves to a bare confirmation, never the terms or the executed document). Returns the server-recorded `signedAt`. Throws
 * `ContractSignError` carrying the backend's human-readable `message` (error
 * envelope, doc §0); `resolveAgain` is set for the statuses where the token's
 * own state moved (§4: 409 contractAlreadySigned / 409 contractExpired / 404
 * unknown) so the page re-resolves to the truthful terminal state.
 */
export async function signContract(
  token: string,
  body: SignContractBody
): Promise<SignContractResult> {
  if (isDevMock()) {
    const fixtures = await import('./fixtures');
    try {
      return { signedAt: fixtures.mockSign(token, body) };
    } catch (err) {
      throw new ContractSignError((err as Error).message, false);
    }
  }
  try {
    const res = await publicApi.post<{ signedAt?: unknown }>(
      `/w2/contract/${encodeURIComponent(token)}/sign`,
      body
    );
    return { signedAt: asString(res.data?.signedAt) };
  } catch (err) {
    throw new ContractSignError(
      extractErrorMessage(err),
      isHttpStatus(err, [404, 409]),
      httpStatus(err),
      isDataChanged(err)
    );
  }
}

/**
 * The contract was edited while this page was open, so the signature was refused against a
 * document the prospect never read. Distinguished from the other re-resolving 409s because
 * the page lands back on Pending (not a terminal state) and has to say why.
 */
function isDataChanged(err: unknown): boolean {
  const data = (err as { response?: { data?: Record<string, unknown> } })
    ?.response?.data;
  // The error envelope carries the code in `errorCode`, or — when the key has no
  // translation — as the (array-wrapped) `errors` entry; `message` is only ever "Conflict.".
  const errors = Array.isArray(data?.errors) ? data.errors : [data?.errors];
  return [data?.errorCode, data?.message, ...errors].some(
    (value) =>
      typeof value === 'string' && value.includes('contractDataChanged')
  );
}

export class ContractSignError extends Error {
  /**
   * True when the contract's state moved under us (signed elsewhere, expired,
   * token rotated) — re-resolve the token instead of showing an inline error.
   */
  readonly resolveAgain: boolean;

  /** HTTP status of the underlying sign failure, for status-only failure logs. */
  readonly status?: number;

  /** The document changed since it was displayed — re-resolves back to Pending, not a terminal state. */
  readonly dataChanged: boolean;

  constructor(
    message: string,
    resolveAgain: boolean,
    status?: number,
    dataChanged = false
  ) {
    super(message);
    this.name = 'ContractSignError';
    this.resolveAgain = resolveAgain;
    this.status = status;
    this.dataChanged = dataChanged;
  }
}

export const CONTRACT_CHANGED_MESSAGE =
  'This contract was updated while you had it open — please review the updated terms and sign again.';

const GENERIC_SIGN_ERROR =
  'We couldn’t record your signature. Please try again, or contact your OneHealth Plus representative.';

function extractErrorMessage(err: unknown): string {
  const message = (err as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;
  return typeof message === 'string' && message.trim() !== ''
    ? message
    : GENERIC_SIGN_ERROR;
}
